import React, { useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { permissionService } from '../../services/permissionService';
import { Permission, PermissionType } from '../../types/permissions';

const moduleOptions = ['Todos', 'Usuários', 'Equipes', 'Perfis', 'Permissões', 'Relatórios', 'Documentos'];
const typeOptions = ['Todos', 'Leitura', 'Escrita', 'Execução', 'Administração'];

export const PermissionsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('Todos');
  const [typeFilter, setTypeFilter] = useState('Todos');
  const [activeTab, setActiveTab] = useState<'ativas' | 'inativas'>('ativas');
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; module: string; type: PermissionType | ''; isActive: boolean }>({
    name: '',
    description: '',
    module: '',
    type: '',
    isActive: false
  });

  useEffect(() => {
    fetchPermissions();
  }, [activeTab]);

  const fetchPermissions = async () => {
    const data =
      activeTab === 'inativas'
        ? await permissionService.getInactivePermissions()
        : await permissionService.getPermissions();
    setPermissions(data);
  };

  const filteredPermissions = permissions.filter(permission =>
    (permission.name.toLowerCase().includes(search.toLowerCase()) ||
      permission.description.toLowerCase().includes(search.toLowerCase())) &&
    (moduleFilter === 'Todos' || permission.module === moduleFilter) &&
    (typeFilter === 'Todos' || permission.type === typeFilter)
  );

  const hasFilters = search.trim() !== '' || moduleFilter !== 'Todos' || typeFilter !== 'Todos';
  const emptyMessage = hasFilters
    ? 'Nenhuma permissão encontrada com os filtros atuais.'
    : activeTab === 'inativas'
      ? 'Não há permissões inativas.'
      : 'Não há permissões ativas.';

  const handleDelete = async (id: string) => {
    await permissionService.deletePermission(id);
    fetchPermissions();
  };

  const handleEdit = (permission: Permission) => {
    setEditingPermission(permission);
    setForm({
      name: permission.name,
      description: permission.description,
      module: permission.module,
      type: permission.type as PermissionType,
      isActive: permission.isActive ?? true
    });
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPermission(null);
    setForm({ name: '', description: '', module: '', type: '', isActive: true });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: name === 'type' ? (value as PermissionType) : value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.module || !form.type) return;
    const payload = { ...form, type: form.type as PermissionType };
    if (editingPermission) {
      await permissionService.updatePermission(editingPermission.id, payload);
    } else {
      await permissionService.createPermission({ ...payload, isSystem: false });
    }
    fetchPermissions();
    handleModalClose();
  };

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gerenciamento de Permissões</h1>
        <Button onClick={() => setIsModalOpen(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nova Permissão
        </Button>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex space-x-2">
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === 'ativas'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveTab('ativas')}
          >
            Ativas
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === 'inativas'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveTab('inativas')}
          >
            Inativas
          </button>
        </div>
        <div className="mb-4 flex justify-between items-center">
          <div className="flex">
            <div className="relative mr-4">
              <input
                type="text"
                placeholder="Buscar permissões..."
                className="border rounded-md pl-10 pr-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <select
              className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
              value={moduleFilter}
              onChange={e => setModuleFilter(e.target.value)}
            >
              {moduleOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <select
              className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Nome</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Descrição</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Módulo</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Tipo</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 px-4 text-center text-gray-500">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                filteredPermissions.map(permission => (
                  <tr key={permission.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{permission.name}</td>
                    <td className="py-3 px-4 text-gray-600">{permission.description}</td>
                    <td className="py-3 px-4">{permission.module}</td>
                    <td className="py-3 px-4">
                      <span className={
                        permission.type === 'Leitura' ? 'bg-blue-100 text-blue-800' :
                        permission.type === 'Escrita' ? 'bg-green-100 text-green-800' :
                        permission.type === 'Execução' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-purple-100 text-purple-800'
                      + ' text-xs font-medium px-2.5 py-0.5 rounded'}>
                        {permission.type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {!permission.isSystem && (
                          <>
                            <Button variant="secondary" size="sm" onClick={() => handleEdit(permission)}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(String(permission.id))}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Modal isOpen={isModalOpen} title={editingPermission ? 'Editar Permissão' : 'Nova Permissão'} onClose={handleModalClose}>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Permissão</label>
            <div className="relative">
              <input 
                name="name" 
                type="text" 
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  form.name.length >= 50 ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`} 
                value={form.name} 
                onChange={handleFormChange} 
                required 
                maxLength={50}
              />
              <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
                {form.name.length}/50
              </div>
            </div>
            {form.name.length >= 50 && (
              <p className="text-xs text-red-500 mt-1">
                Limite máximo de 50 caracteres atingido
              </p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea name="description" className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={form.description} onChange={handleFormChange} required></textarea>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Módulo</label>
              <select name="module" className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.module} onChange={handleFormChange} required>
                <option value="">Selecione um módulo</option>
                {moduleOptions.filter(opt => opt !== 'Todos').map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select name="type" className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.type} onChange={handleFormChange} required>
                <option value="">Selecione um tipo</option>
                {typeOptions.filter(opt => opt !== 'Todos').map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4 flex items-center">
            <input
              id="isActive"
              name="isActive"
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={form.isActive}
              onChange={e => setForm({ ...form, isActive: e.target.checked })}
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
              Ativa
            </label>
          </div>
          <div className="flex justify-end">
            <Button variant="secondary" type="button" className="mr-2" onClick={handleModalClose}>Cancelar</Button>
            <Button type="submit">Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}; 
